/**
 * The dead-session signal, and when it may force a trip back to Keycloak.
 *
 * A Keycloak session that dies while a tab sits idle is invisible until the
 * next request: react-oidc-context leaves `isAuthenticated` true when silent
 * renew fails, so every page renders its own load error instead of sending
 * the user back to sign in. AuthContext registers what to do about it here,
 * and every path that talks to the api reports through here, so one place
 * decides what a 401 means.
 */

let sessionExpiredHandler: (() => void) | null = null

/** AuthContext registers the app's response to a dead session. */
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  sessionExpiredHandler = handler
}

/**
 * Report an api response status, waking the sign-in redirect on a 401.
 *
 * Every browser route on the api authenticates through one dependency
 * (api/app/dependencies/auth.py), so a 401 means the Keycloak token is
 * missing, expired or rejected. It is never a per-resource permission
 * problem, which the api answers with 403.
 *
 * Both fetch wrappers in api/client.ts call this, and so does the error path
 * the streaming chat hooks share (readErrorDetail in lib/sse.ts) — they issue
 * their own `fetch` so SSE bodies stream, which would otherwise leave two
 * endpoints showing a 401 instead of sending the user to sign in.
 */
export function notifyIfSessionExpired(status: number): void {
  if (status === 401) {
    sessionExpiredHandler?.()
  }
}

/** How long one forced re-authentication suppresses the next. */
export const REAUTH_COOLDOWN_MS = 30_000

const REAUTH_AT_KEY = 'platform-v2-reauth-at'

/**
 * Whether a 401 may force another trip to Keycloak.
 *
 * A real expiry fires this once and the user signs in, so the cooldown is
 * never in the way. It exists for the case where the api rejects a token
 * Keycloak just minted (a bad deploy, a badly skewed clock): without it the
 * browser bounces between the two forever. A `lastAt` ahead of `now` is a
 * clock that moved backwards, not a recent attempt.
 */
export function reauthAllowed({
  now,
  lastAt,
}: {
  now: number
  lastAt: number | null
}): boolean {
  if (lastAt === null || Number.isNaN(lastAt)) return true
  if (lastAt > now) return true
  return now - lastAt >= REAUTH_COOLDOWN_MS
}

/**
 * Claim the right to force a re-authentication, recording the attempt.
 *
 * sessionStorage, not module state: the trip to Keycloak reloads the page, so
 * an in-memory marker would be gone exactly when the loop it guards against
 * would begin.
 */
export function claimReauthAttempt(now: number): boolean {
  const stored = sessionStorage.getItem(REAUTH_AT_KEY)
  if (!reauthAllowed({ now, lastAt: stored === null ? null : Number(stored) })) {
    return false
  }
  sessionStorage.setItem(REAUTH_AT_KEY, now.toString())
  return true
}
